import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Snackbar } from '@mui/material'
import socket from '../utils/socket'
import { useAuthContext } from './auth'
import { startRing, stopRing } from '../utils/ringtone'
import { computeKeyCheck, generatePasscode, normalizePasscode } from '../utils/e2ee'

// "encryption" is the current LiveKit option name. With an older livekit-client use 'e2ee'.
const E2EE_OPTION = 'encryption'

// Inputs that record what the computer plays: they always cause echo
const LOOPBACK_MIC = /stereo mix|loopback|what u hear|wave out|monitor of/i

const CallContext = createContext(null)

export const useCall = () => {
  const ctx = useContext(CallContext)
  if (!ctx) throw new Error('useCall must be used inside <CallProvider>')
  return ctx
}

const RING_TIMEOUT = 30000
const CAMERA_KEY = 'call.cameraId'
const MIC_KEY = 'call.micId'
const CAPTION_LANG_KEY = 'call.captionLang'
const CAPTION_LINGER = 5000      // a caption stays this long after its last update
const SpeechRec = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null

const ICE_SERVERS = (() => {
  try {
    return JSON.parse(process.env.REACT_APP_ICE_SERVERS)
  } catch {
    return [{ urls: 'stun:stun.l.google.com:19302' }]
  }
})()

const RECONNECT_GRACE = 4000        // wait this long after a drop before restarting ICE
const RECONNECT_RETRY = 6000        // then retry every 6 s
const RECONNECT_GIVE_UP = 25000     // give up if still not connected

const SFU_JOIN_MUTED_AFTER = 8   // in a big meeting, joiners start with mic and camera off

const isAgentParticipant = (p, lk) =>
  Boolean(p?.identity?.startsWith('agent-')) ||
  Boolean(p?.identity?.startsWith('EG_')) ||
  p?.kind === lk?.ParticipantKind?.AGENT ||
  p?.kind === lk?.ParticipantKind?.EGRESS

// Set REACT_APP_FORCE_RELAY=true to test TURN (forces all media through the relay)
const rtcConfig = (servers) => ({
  iceServers: servers || ICE_SERVERS,
  ...(process.env.REACT_APP_FORCE_RELAY === 'true' ? { iceTransportPolicy: 'relay' } : {}),
})

const callError = (code) =>
  ({
    busy: 'The user is on another call.',
    offline: 'The user is offline.',
    'already-in-call': 'You are already in a call.',
  }[code] || 'Unable to start the call.')

const groupCallError = (code) =>
  ({
    'already-in-call': 'You are already in a call.',
    'not-member': 'You are not a member of this group.',
    full: 'This call is full.',
    livekit: 'The meeting server is unavailable.',
    ended: 'That call has already ended.',
    'already-joined': 'You are already in this call from another window.',
    timeout: 'The server did not respond. Please try again.',
    removed: 'You were removed from this meeting by the host.',
    'private-unavailable': 'Private meetings are only for large groups. Small group calls are already encrypted between the participants.',
    'invalid-key': 'The meeting key was not accepted.',
  }[code] || 'Unable to join the call.')

const mediaError = (err) => {
  if (err?.name === 'NotAllowedError') return 'Microphone/camera permission was denied.'
  if (err?.name === 'NotReadableError') {
    return 'That device is in use by another app or browser. Choose a different one from the menu next to its button.'
  }
  if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
    return 'That device is not available (unplugged, or held by another app). Choose another one from the menu.'
  }
  return err?.message || 'Unable to access the microphone/camera.'
}

const hostError = (code) =>
  ({
    'not-host': 'Only a host can do that.',
    unsupported: 'Host controls work in large meetings only.',
    ended: 'The meeting has ended.',
    'invalid-target': 'That person is not available for this action.',
    livekit: 'The meeting server did not respond.',
    timeout: 'The server did not respond. Please try again.',
    'recording-unavailable': 'Recording is not set up on this server.',
    'already-recording': 'The meeting is already being recorded.',
    'not-recording': 'The meeting is not being recorded.',
    'recording-failed': 'The recording could not be started. Check the recording service.',    
    'recording-timeout': 'The recording service did not answer. Check that Egress is running.',
    'private-meeting': 'Recording is not available in private meetings.',
  }[code] || 'Unable to do that.')

// Each browser remembers its own preferred devices
const readSaved = (key) => {
  try {
    return localStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

const save = (key, id) => {
  try {
    if (id) localStorage.setItem(key, id)
    else localStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

const videoConstraint = () => {
  const saved = readSaved(CAMERA_KEY)
  return saved ? { deviceId: { ideal: saved } } : true
}

const audioConstraint = () => {
  const saved = readSaved(MIC_KEY)
  return {
    echoCancellation: true, noiseSuppression: true, autoGainControl: true,
    ...(saved ? { deviceId: { ideal: saved } } : {}),
  }
}

const trackDeviceId = (track) => track?.getSettings?.().deviceId || ''

// A webcam can only be used by one app at a time. If the preferred camera is busy or gone,
// try the other cameras before giving up.
const openCamera = async ({ preferredId = '', fallback = true } = {}) => {
  const attempt = (constraint) => navigator.mediaDevices.getUserMedia({ video: constraint })

  try {
    return await attempt(preferredId ? { deviceId: { ideal: preferredId } } : true)
  } catch (firstError) {
    const retryable = ['NotReadableError', 'NotFoundError', 'OverconstrainedError', 'AbortError']
    if (!fallback || !retryable.includes(firstError?.name)) throw firstError

    const devices = await navigator.mediaDevices.enumerateDevices()
    const others = devices.filter(
      (d) => d.kind === 'videoinput' && d.deviceId && d.deviceId !== preferredId
    )

    for (const d of others) {
      try {
        return await attempt({ deviceId: { exact: d.deviceId } })
      } catch {
        /* this one is busy too: try the next */
      }
    }

    throw firstError
  }
}

// Large meetings: turn the camera on; if the preferred one is busy (another browser or app),
// try the other cameras before giving up
const enableSfuCamera = async (lp, preferredId = '') => {
  const tried = new Set()

  const attempt = async (deviceId) => {
    tried.add(deviceId)
    await lp.setCameraEnabled(true, deviceId ? { deviceId: { exact: deviceId } } : undefined)
  }

  let firstError
  try {
    await attempt(preferredId)
    return
  } catch (err) {
    firstError = err
    console.warn('[sfu] preferred camera failed', err?.name, err?.message)
    if (err?.name === 'NotAllowedError') throw err
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  const cameras = devices.filter(
    (d) => d.kind === 'videoinput' && d.deviceId && !/\bIR\b|infrared/i.test(d.label)   // skip Windows Hello IR cameras
  )

  for (const d of cameras) {
    if (tried.has(d.deviceId)) continue
    try {
      await attempt(d.deviceId)
      console.log('[sfu] using camera', d.label)
      return
    } catch (err) {
      console.warn('[sfu] camera failed', d.label, err?.name)
    }
  }

  throw firstError
}

// socket.emit with an acknowledgement, and a timeout so the UI can never hang
const emitWithAck = (event, payload, timeoutMs = 8000) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: 'timeout' }), timeoutMs)
    socket.emit(event, payload, (res) => {
      clearTimeout(timer)
      resolve(res)
    })
  })

export const CallProvider = ({ children }) => {
  const { auth } = useAuthContext()

  // One call at a time. call.kind is 'direct' (1:1, kind undefined) or 'group'
  const [call, setCall] = useState(null)
  const [localStream, setLocalStream] = useState(null)         // my microphone + camera (never the screen)
  const [remoteStream, setRemoteStream] = useState(null)       // 1:1: the other person
  const [muted, setMuted] = useState(false)
  const [cameraOff, setCameraOff] = useState(true)              // my camera
  const [remoteCamera, setRemoteCamera] = useState(false)       // 1:1: the other person's camera
  const [cameras, setCameras] = useState([])
  const [cameraId, setCameraId] = useState(() => readSaved(CAMERA_KEY))
  const [mics, setMics] = useState([])
  const [micId, setMicId] = useState(() => readSaved(MIC_KEY))
  const [notice, setNotice] = useState('')
  const [screenSharing, setScreenSharing] = useState(false)             // I am presenting
  const [remoteScreenSharing, setRemoteScreenSharing] = useState(false) // 1:1: the other person is presenting
  const [hosts, setHosts] = useState([])                   // user ids with host rights in this call
  const [recording, setRecording] = useState(null)         // { startedAt, by } while the meeting is recorded
  const [canRecord, setCanRecord] = useState(false)        // the server has recording set up

  // Group calls
  const [participants, setParticipants] = useState([])        // [{ userId, name }] including me
  const [peerStreams, setPeerStreams] = useState({})          // userId -> MediaStream
  const [peerMedia, setPeerMedia] = useState({})              // userId -> { camera, muted, screenShare }
  const [activeGroupCalls, setActiveGroupCalls] = useState({}) // groupId -> { callId, type, participants, ... }
  const [incomingGroup, setIncomingGroup] = useState(null)     // ring card for a group call

  const callRef = useRef(null)          // always-current call (safe to read in socket handlers)
  const pcRef = useRef(null)            // 1:1 connection
  const peersRef = useRef(new Map())    // group: userId -> { pc, audioSender, videoSender, pending }
  const localRef = useRef(null)         // microphone (+ camera when it is on). Never contains the screen.
  const screenTrackRef = useRef(null)   // my screen track while presenting
  const screenBusyRef = useRef(false)
  const audioSenderRef = useRef(null)   // 1:1 audio slot
  const videoSenderRef = useRef(null)   // 1:1 video slot
  const mutedRef = useRef(false)
  const cameraOnRef = useRef(false)
  const cameraBusyRef = useRef(false)
  const micBusyRef = useRef(false)
  const pendingIce = useRef([])
  const ringTimer = useRef(null)
  const groupRingTimer = useRef(null)
  const activeRef = useRef({})
  activeRef.current = activeGroupCalls
  const [speaking, setSpeaking] = useState({})   // { me: true, [userId]: true }

  const remoteStreamRef = useRef(null)
  remoteStreamRef.current = remoteStream
  const peerStreamsRef = useRef({})
  peerStreamsRef.current = peerStreams

  const iceRef = useRef({ servers: null, expires: 0 })   // TURN credentials from the server
  const reconnectingRef = useRef(new Set())               // 'direct', a userId, or 'socket'
  const restartDirectRef = useRef(null)                   // restarts ICE of the 1:1 connection
  const socketGiveUpRef = useRef(null)
  const handRaisedRef = useRef(false)
  const [handRaised, setHandRaised] = useState(false)

  // Live captions
  const [captionsOn, setCaptionsOn] = useState(false)           // I read captions
  const [captionsActive, setCaptionsActive] = useState(false)   // somebody in the call reads captions
  const [captions, setCaptions] = useState({})                  // speakerId -> { name, text, final, at }
  const [captionLang, setCaptionLang] = useState(() => readSaved(CAPTION_LANG_KEY) || navigator.language || 'en-US')
  const [captionAgent, setCaptionAgent] = useState(false)    // server-side captions available in this call

  const [sfu, setSfu] = useState(null)          // { participants: [...] } during a large (SFU) meeting
  const sfuRoomRef = useRef(null)
  const sfuLibRef = useRef(null)
  const sfuTimerRef = useRef(null)
  const sfuAudioRef = useRef(new Set())         // hidden <audio> elements of remote participants

  const [isPrivate, setIsPrivate] = useState(false)             // this meeting is end-to-end encrypted
  const [passcode, setPasscode] = useState(null)               // kept in memory only
  const [passcodePrompt, setPasscodePrompt] = useState(null)   // { info, withVideo, error }: asking a joiner
  const [passcodeShare, setPasscodeShare] = useState(null)     // shown to the host right after starting
  const lastEncryptionNotice = useRef(0)

  const updateCall = useCallback((next) => {
    callRef.current = next
    setCall(next)
  }, [])

  // ---------- Live captions ----------
  const pushCaption = useCallback((userId, name, text, final) => {
    if (!userId || !text) return
    setCaptions((prev) => ({ ...prev, [String(userId)]: { name, text, final: Boolean(final), at: Date.now() } }))
  }, [])

  const toggleCaptions = () => {
    const c = callRef.current
    const next = !captionsOn
    setCaptionsOn(next)
    if (!next) setCaptions({})
    if (c) socket.emit('call:captions-want', { callId: c.callId, on: next })
  }

  const changeCaptionLang = (lang) => {
    save(CAPTION_LANG_KEY, lang)
    setCaptionLang(lang)
    sfuRoomRef.current?.localParticipant?.setAttributes({ lang }).catch(() => {})
  }

  // Host controls (large meetings): 'mute-all' | 'mute' | 'remove' | 'make-host'
  const hostAction = useCallback(async (action, targetId) => {
    const c = callRef.current
    if (!c) return { ok: false }

    const res = await emitWithAck('gcall:host-action', { callId: c.callId, action, targetId }, 20000)

    if (!res?.ok) setNotice(res?.detail ? `${hostError(res.error)} (${res.detail})` : hostError(res?.error))
    else if (action === 'mute-all') setNotice(res.count ? `Muted ${res.count} people.` : 'Everyone is already muted.')
 
    return res
  }, [])

  // Shows "Reconnecting…" while any connection (peer or socket) is down
  const markReconnecting = useCallback((key, on) => {
    const set = reconnectingRef.current
    if (on) set.add(key)
    else set.delete(key)

    const c = callRef.current
    const flag = set.size > 0
    if (c && Boolean(c.reconnecting) !== flag) updateCall({ ...c, reconnecting: flag })
  }, [updateCall])

  // Fresh TURN credentials from the server (cached; falls back to REACT_APP_ICE_SERVERS)
  const loadIceServers = useCallback(async () => {
    if (Date.now() < iceRef.current.expires) return

    const res = await Promise.race([
      emitWithAck('call:ice', {}),
      new Promise((resolve) => setTimeout(() => resolve(null), 2500)),
    ])

    if (res?.ok && res.iceServers?.length) {
      // refresh when half of the lifetime has passed
      iceRef.current = { servers: res.iceServers, expires: Date.now() + (res.ttl || 3600) * 500 }
    } else {
      iceRef.current = { servers: null, expires: Date.now() + 60000 }   // don't wait again for a minute
    }
  }, [])

  const setCamera = useCallback((on) => {
    cameraOnRef.current = on
    setCameraOff(!on)
  }, [])

  // Tell the other side(s) my camera / mute / screen-share state
  const announceMedia = useCallback(() => {
    const c = callRef.current
    if (!c) return
    if (c.mode === 'sfu') return

    const screenShare = Boolean(screenTrackRef.current)

    if (c.kind === 'group') {
      socket.emit('gcall:media', {
        callId: c.callId,
        camera: cameraOnRef.current,
        muted: mutedRef.current,
        screenShare,
        handRaised: handRaisedRef.current,
      })
    } else {
      socket.emit('call:signal', {
        callId: c.callId,
        data: { camera: cameraOnRef.current, screenShare },
      })
    }
  }, [])

  // Which track should a video slot carry right now? The screen wins over the camera.
  const getOutgoingVideoTrack = useCallback(
    () => screenTrackRef.current || localRef.current?.getVideoTracks()[0] || null,
    []
  )

  // All the places my audio / video is being sent to (1:1: one, group: one per participant)
  const audioSenders = () =>
    [audioSenderRef.current, ...[...peersRef.current.values()].map((p) => p.audioSender)].filter(Boolean)

  const videoSenders = () =>
    [videoSenderRef.current, ...[...peersRef.current.values()].map((p) => p.videoSender)].filter(Boolean)

  // ---------- Device lists ----------
  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()

      setCameras(
        devices
          .filter((d) => d.kind === 'videoinput')
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }))
      )

      setMics(
        devices
          .filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications' && !LOOPBACK_MIC.test(d.label))
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }))
      )
    } catch (err) {
      console.error('Unable to list devices', err)
    }
  }, [])

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return
    refreshDevices()
    navigator.mediaDevices.addEventListener('devicechange', refreshDevices)
    return () => navigator.mediaDevices.removeEventListener('devicechange', refreshDevices)
  }, [refreshDevices])

  // Stop everything and reset the UI
  const cleanup = useCallback(() => {
    clearTimeout(ringTimer.current)

    if (pcRef.current) {
      pcRef.current.ontrack = null
      pcRef.current.onicecandidate = null
      pcRef.current.onconnectionstatechange = null
      pcRef.current.close()
      pcRef.current = null
    }

    peersRef.current.forEach((p) => {
      clearTimeout(p.retryTimer)
      clearTimeout(p.giveUpTimer)
      p.pc.ontrack = null
      p.pc.onicecandidate = null
      p.pc.onconnectionstatechange = null
      p.pc.close()
    })
    peersRef.current.clear()

    // Stop the screen (this also removes the browser's "Sharing your screen" bar)
    if (screenTrackRef.current) {
      screenTrackRef.current.onended = null
      screenTrackRef.current.stop()
      screenTrackRef.current = null
    }
    screenBusyRef.current = false

    localRef.current?.getTracks().forEach((t) => t.stop())
    localRef.current = null
    audioSenderRef.current = null
    videoSenderRef.current = null
    pendingIce.current = []
    cameraBusyRef.current = false
    micBusyRef.current = false
    cameraOnRef.current = false
    mutedRef.current = false

    reconnectingRef.current.clear()
    restartDirectRef.current = null
    clearTimeout(socketGiveUpRef.current)
    handRaisedRef.current = false
    setHandRaised(false)

    setCaptionsOn(false)
    setCaptionsActive(false)
    setCaptions({})
    setCaptionAgent(false)

    setLocalStream(null)
    setRemoteStream(null)
    setMuted(false)
    setCameraOff(true)
    setRemoteCamera(false)
    setScreenSharing(false)
    setRemoteScreenSharing(false)
    setParticipants([])
    setPeerStreams({})
    setPeerMedia({})
    updateCall(null)
    setHosts([])
    setRecording(null)
    setCanRecord(false)
    setIsPrivate(false)
    setPasscode(null)
    setPasscodeShare(null)
    
    // Large meeting (SFU)
    clearTimeout(sfuTimerRef.current)
    sfuTimerRef.current = null
    const sfuRoom = sfuRoomRef.current
    sfuRoomRef.current = null
    if (sfuRoom) {
      sfuRoom.removeAllListeners()
      sfuRoom.disconnect()
    }
    sfuAudioRef.current.forEach((el) => { el.pause(); el.srcObject = null; el.remove() })
    sfuAudioRef.current.clear()
    setSfu(null)
  }, [updateCall])

  const finish = useCallback((message) => {
    cleanup()
    if (message) setNotice(message)
  }, [cleanup])

  // End (1:1) or leave (group) the call
  const hangUp = useCallback((reason = 'ended') => {
    const c = callRef.current
    if (c?.kind === 'group') socket.emit('gcall:leave', { callId: c.callId })
    else if (c) socket.emit('call:end', { callId: c.callId, reason })
    cleanup()
  }, [cleanup])

  // Warn when echo cancellation is not active or the microphone records the computer's sound
  const checkMicrophone = (track) => {
    if (!track) return
    const s = track.getSettings?.() || {}
    console.log('[audio] microphone', track.label, s)      // echoCancellation should be true

    if (LOOPBACK_MIC.test(track.label || '')) {
      setNotice('This microphone records the computer\'s sound ("Stereo Mix"). Choose a real microphone to avoid echo.')
    } else if (s.echoCancellation === false) {
      setNotice('Echo cancellation is off for this microphone. Use a headset to avoid echo.')
    }
  }

  const getMedia = async (type) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Calls need HTTPS (or localhost) to use the microphone and camera.')
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraint(),
        video: type === 'video' ? videoConstraint() : false,
      })
    } catch (err) {
      if (type !== 'video') throw err

      // The camera is busy, missing or blocked: keep the microphone, then try every camera
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint() })
      try {
        const cam = await openCamera({ preferredId: readSaved(CAMERA_KEY) })
        cam.getVideoTracks().forEach((t) => stream.addTrack(t))
      } catch (camErr) {
        setNotice(`${mediaError(camErr)} Continuing with audio only.`)
      }
    }

    localRef.current = stream
    setLocalStream(stream)

        const mic = stream.getAudioTracks()[0]
    checkMicrophone(mic)
    if (mic) setMicId(trackDeviceId(mic))

    const cam = stream.getVideoTracks()[0]
    setCamera(Boolean(cam))
    if (cam) setCameraId(trackDeviceId(cam))

    refreshDevices()   // device names are only available after permission is granted
    return stream
  }

  // ======================================================================
  // Large meetings (SFU / LiveKit): media goes through the server, not peer to peer
  // ======================================================================
  const takeSfuSnapshot = useCallback(() => {
    const room = sfuRoomRef.current
    const Track = sfuLibRef.current?.Track
    if (!room || !Track) return

    const describe = (p, isLocal) => ({
      id: p.identity,
      name: p.name || p.identity,
      isLocal,
      mic: p.isMicrophoneEnabled,
      camera: p.isCameraEnabled,
      screen: p.isScreenShareEnabled,
      speaking: p.isSpeaking,
      hand: isLocal ? handRaisedRef.current : p.attributes?.hand === '1',
      cameraTrack: p.getTrackPublication(Track.Source.Camera)?.videoTrack || null,
      screenTrack: p.getTrackPublication(Track.Source.ScreenShare)?.videoTrack || null,
    })

    const lp = room.localParticipant
    const remotes = [...(room.remoteParticipants ?? room.participants).values()]
      .filter((p) => !isAgentParticipant(p, sfuLibRef.current))

    setSfu({ participants: [describe(lp, true), ...remotes.map((p) => describe(p, false))] })

    // The shared buttons (mute / camera / screen) follow the real state
    mutedRef.current = !lp.isMicrophoneEnabled
    setMuted(!lp.isMicrophoneEnabled)
    setCamera(lp.isCameraEnabled)
    setScreenSharing(lp.isScreenShareEnabled)
  }, [setCamera])

  // Many events arrive at once in a big meeting: update the UI at most every 150 ms
  const scheduleSfuSnapshot = useCallback(() => {
    if (sfuTimerRef.current) return
    sfuTimerRef.current = setTimeout(() => {
      sfuTimerRef.current = null
      takeSfuSnapshot()
    }, 150)
  }, [takeSfuSnapshot])

  const connectSfu = async ({ url, token, mic, camera, passphrase }) => {
    const lk = await import('livekit-client')      // downloaded only when a large meeting starts
    sfuLibRef.current = lk
    const { Room, RoomEvent, Track } = lk

    // Private meeting: end-to-end encryption with a key that only the participants know
    let keyProvider = null
    let e2eeOptions = {}

    if (passphrase) {
      if (lk.isE2EESupported?.() === false) {
        throw new Error('This browser can not join private meetings. Use a current Chrome, Edge, Firefox or Safari.')
      }
      keyProvider = new lk.ExternalE2EEKeyProvider()
      const worker = new Worker(new URL('livekit-client/e2ee-worker', import.meta.url))
      e2eeOptions = { [E2EE_OPTION]: { keyProvider, worker } }
    }

    // adaptiveStream: only the videos that are on screen are downloaded, at the size they are shown
    const room = new Room({
      adaptiveStream: true, dynacast: true,
      audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      ...e2eeOptions,
    })
    sfuRoomRef.current = room

    // Server-side captions (agent): one text stream per sentence, labelled with the speaker
    room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      try {
        const identity = participantInfo?.identity
        if (!identity) return

        const text = (await reader.readAll()).trim()
        if (!text) return

        const isMe = identity === room.localParticipant.identity
        const speaker = isMe ? room.localParticipant : (room.remoteParticipants ?? room.participants).get(identity)
        const final = reader.info?.attributes?.['lk.transcription_final'] === 'true'

        pushCaption(identity, isMe ? 'You' : speaker?.name || identity, text, final)
      } catch (err) {
        console.error('[sfu] caption stream failed', err)
      }
    })

    const refresh = () => scheduleSfuSnapshot()

    room
      .on(RoomEvent.ParticipantConnected, refresh)
      .on(RoomEvent.ParticipantDisconnected, refresh)
      .on(RoomEvent.TrackPublished, refresh)
      .on(RoomEvent.TrackUnpublished, refresh)
      .on(RoomEvent.TrackMuted, refresh)
      .on(RoomEvent.TrackUnmuted, refresh)
      .on(RoomEvent.LocalTrackPublished, refresh)
      .on(RoomEvent.LocalTrackUnpublished, refresh)
      .on(RoomEvent.ParticipantAttributesChanged, refresh)
      .on(RoomEvent.ActiveSpeakersChanged, refresh)
      .on(RoomEvent.EncryptionError, () => {
        const now = Date.now()
        if (now - lastEncryptionNotice.current < 15000) return
        lastEncryptionNotice.current = now
        setNotice('Some audio or video could not be decrypted. Check that everybody uses the same passcode.')
      })      
      .on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
        if (topic !== 'caption') return
        try {
          const d = JSON.parse(new TextDecoder().decode(payload))
          pushCaption(participant?.identity, participant?.name || participant?.identity, d.text, d.final)
        } catch { /* ignore malformed data */ }
      })      
      .on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach()
          el.style.display = 'none'
          document.body.appendChild(el)
          sfuAudioRef.current.add(el)
        }
        refresh()
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach((el) => {
          el.remove()
          sfuAudioRef.current.delete(el)
        })
        refresh()
      })
      .on(RoomEvent.Reconnecting, () => markReconnecting('sfu', true))
      .on(RoomEvent.Reconnected, () => markReconnecting('sfu', false))
      .on(RoomEvent.Disconnected, () => {
        if (sfuRoomRef.current !== room) return      // we left on purpose
        hangUp('ended')
        setNotice('You were disconnected from the meeting.')
      })

    // Temporary debug logging
    room
      .on(RoomEvent.ConnectionStateChanged, (s) => console.log('[sfu] state', s))
      .on(RoomEvent.ParticipantConnected, (p) => console.log('[sfu] joined', p.name || p.identity))
      .on(RoomEvent.TrackSubscribed, (t, pub, p) => console.log('[sfu] subscribed', t.kind, p.name || p.identity))
      .on(RoomEvent.TrackSubscriptionFailed, (sid, p) => console.warn('[sfu] subscription failed', sid, p?.name))
      .on(RoomEvent.MediaDevicesError, (e) => console.error('[sfu] media error', e))

    if (passphrase) {
      await keyProvider.setKey(passphrase)
      await room.setE2EEEnabled(true)
    }

    try {
      await room.connect(url, token)
      room.localParticipant.setAttributes({ lang: captionLang }).catch(() => {})
    } catch (err) {
      sfuRoomRef.current = null
      room.removeAllListeners()
      throw err
    }

    // Never run a "private" meeting without encryption. If the library ignored the option, stop here.
    if (passphrase && room.isE2EEEnabled === false) {
      await room.disconnect()
      throw new Error('Encryption could not be started. The meeting was not joined.')
    }

    room.startAudio().catch(() => {})

    try {
      await room.localParticipant.setMicrophoneEnabled(mic)
    } catch (err) {
      setNotice(mediaError(err))     // joined anyway: listening still works
    }
    checkMicrophone(room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack)
    
    if (camera) {
      try {
        await enableSfuCamera(room.localParticipant, readSaved(CAMERA_KEY))
      } catch (err) {
        setNotice(`${mediaError(err)} Joined without a camera.`)
      }
    }

    // Use the devices this browser remembers
    const savedMic = readSaved(MIC_KEY)
    if (mic && savedMic) room.switchActiveDevice('audioinput', savedMic).catch(() => {})

    refreshDevices()
    takeSfuSnapshot()
  }

  // ======================================================================
  // 1:1 calls
  // ======================================================================

  // Callee side: the video slot comes with the caller's offer. Bind to THAT slot
  const bindVideoSlot = useCallback(async (pc) => {
    const tr = pc.getTransceivers().find((t) => t.receiver?.track?.kind === 'video')
    if (!tr) return

    tr.direction = 'sendrecv'
    videoSenderRef.current = tr.sender

    const track = getOutgoingVideoTrack()
    if (track) await tr.sender.replaceTrack(track)
  }, [getOutgoingVideoTrack])

  const createPeer = useCallback((callId, stream, role) => {
    const pc = new RTCPeerConnection(rtcConfig(iceRef.current.servers))

    stream.getAudioTracks().forEach((t) => {
      audioSenderRef.current = pc.addTrack(t, stream)
    })

    if (role === 'caller') {
      const transceiver = pc.addTransceiver('video', { direction: 'sendrecv', streams: [stream] })
      videoSenderRef.current = transceiver.sender

      const track = getOutgoingVideoTrack()
      if (track) transceiver.sender.replaceTrack(track).catch((err) => console.error(err))
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) socket.emit('call:signal', { callId, data: { candidate: e.candidate } })
    }

    const remote = new MediaStream()
    pc.ontrack = (e) => {
      if (!remote.getTracks().some((t) => t.id === e.track.id)) remote.addTrack(e.track)
      setRemoteStream(remote)
    }

    // ICE restart: only the caller sends the new offer, so the two sides never collide
    const restartIce = async () => {
      if (pcRef.current !== pc || role !== 'caller' || pc.signalingState !== 'stable') return
      try {
        const offer = await pc.createOffer({ iceRestart: true })
        await pc.setLocalDescription(offer)
        socket.emit('call:signal', { callId, data: { sdp: pc.localDescription } })
      } catch (err) {
        console.error('ICE restart failed', err)
      }
    }
    restartDirectRef.current = restartIce

    let retryTimer = null
    let giveUpTimer = null

    const clearRecovery = () => {
      clearTimeout(retryTimer)
      clearTimeout(giveUpTimer)
      retryTimer = null
      giveUpTimer = null
    }

    const scheduleRestart = (delay) => {
      retryTimer = setTimeout(() => {
        if (pcRef.current !== pc || pc.connectionState === 'connected') return
        restartIce()
        scheduleRestart(RECONNECT_RETRY)
      }, delay)
    }

    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return
      const state = pc.connectionState

      if (state === 'connected') {
        clearRecovery()
        markReconnecting('direct', false)

        if (callRef.current && callRef.current.status !== 'connected') {
          socket.emit('call:connected', { callId })
          announceMedia()
          updateCall({ ...callRef.current, status: 'connected', connectedAt: Date.now() })
        }
        return
      }

      if (state !== 'disconnected' && state !== 'failed') return

      // Never connected: restarting won't help, this network needs a TURN server
      if (callRef.current?.status !== 'connected') {
        if (state === 'failed') {
          hangUp('ended')
          setNotice('Connection failed. A TURN server may be needed on this network.')
        }
        return
      }

      // Was connected: try to recover before giving up
      markReconnecting('direct', true)
      if (!retryTimer) scheduleRestart(state === 'failed' ? 0 : RECONNECT_GRACE)
      if (!giveUpTimer) {
        giveUpTimer = setTimeout(() => {
          if (pcRef.current === pc && pc.connectionState !== 'connected') {
            hangUp('ended')
            setNotice('The connection was lost.')
          }
        }, RECONNECT_GIVE_UP)
      }
    }

    pcRef.current = pc
    return pc
    }, [updateCall, hangUp, announceMedia, getOutgoingVideoTrack, markReconnecting])

  const startCall = async (user, type) => {
    if (callRef.current) return setNotice('You are already in a call.')

    try {
      await loadIceServers()

      const stream = await getMedia(type)
      const actualType = stream.getVideoTracks().length > 0 ? 'video' : 'audio'

      const ack = await new Promise((resolve) =>
        socket.emit('call:invite', { toUserId: user._id, type: actualType }, resolve)
      )

      if (!ack?.ok) {
        finish(callError(ack?.error))
        return
      }

      updateCall({
        callId: ack.callId,
        peerId: user._id,
        peerName: user.name,
        peerAvatar: ack.peerAvatar || user.avatar || null,
        type: actualType,
        direction: 'out',
        status: 'calling',
      })

      ringTimer.current = setTimeout(() => {
        hangUp('missed')
        setNotice(`${user.name} didn't answer.`)
      }, RING_TIMEOUT)
    } catch (err) {
      finish(mediaError(err))
    }
  }

  const acceptCall = async (withVideo) => {
    const c = callRef.current
    if (!c || c.kind === 'group' || c.status !== 'ringing') return

    const video = typeof withVideo === 'boolean' ? withVideo : c.type === 'video'

    try {
      await loadIceServers()

      const stream = await getMedia(video ? 'video' : 'audio')

      if (callRef.current?.callId !== c.callId) {
        stream.getTracks().forEach((t) => t.stop())
        localRef.current = null
        setLocalStream(null)
        return
      }

      createPeer(c.callId, stream, 'callee')
      updateCall({ ...c, status: 'connecting' })
      socket.emit('call:accept', { callId: c.callId })
    } catch (err) {
      socket.emit('call:reject', { callId: c.callId })
      finish(mediaError(err))
    }
  }

  const rejectCall = () => {
    const c = callRef.current
    if (c) socket.emit('call:reject', { callId: c.callId })
    cleanup()
  }

  // ======================================================================
  // Group calls (mesh)
  // ======================================================================

  const removePeer = useCallback((userId) => {
    const peer = peersRef.current.get(userId)
    if (!peer) return

    clearTimeout(peer.retryTimer)
    clearTimeout(peer.giveUpTimer)

    peer.pc.ontrack = null
    peer.pc.onicecandidate = null
    peer.pc.onconnectionstatechange = null
    peer.pc.close()
    peersRef.current.delete(userId)

    markReconnecting(userId, false)

    setPeerStreams((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
    setPeerMedia((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
  }, [markReconnecting])

  // Only the side that sent the original offer restarts ICE
  const restartPeerIce = useCallback(async (callId, userId) => {
    const peer = peersRef.current.get(userId)
    const c = callRef.current
    if (!peer || peer.role !== 'offerer' || !c || c.callId !== callId) return
    if (peer.pc.signalingState !== 'stable') return   // a negotiation is already in progress

    try {
      const offer = await peer.pc.createOffer({ iceRestart: true })
      await peer.pc.setLocalDescription(offer)
      socket.emit('gcall:signal', { callId, to: userId, data: { sdp: peer.pc.localDescription } })
    } catch (err) {
      console.error('ICE restart failed', err)
    }
  }, [])

  // Restart ICE on every connection (after the network came back or changed)
  const restartAllIce = useCallback(() => {
    const c = callRef.current
    if (!c) return

    if (c.kind === 'group') peersRef.current.forEach((_, userId) => restartPeerIce(c.callId, userId))
    else restartDirectRef.current?.()
  }, [restartPeerIce])

  useEffect(() => {
    window.addEventListener('online', restartAllIce)
    return () => window.removeEventListener('online', restartAllIce)
  }, [restartAllIce])

  // role 'offerer' = I just joined and I send the offer. 'answerer' = someone joined after me.
  const createGroupPeer = useCallback((callId, userId, role) => {
    const stream = localRef.current
    const pc = new RTCPeerConnection(rtcConfig(iceRef.current.servers))
    const peer = { pc, role, audioSender: null, videoSender: null, pending: [], retryTimer: null, giveUpTimer: null }

    // Audio: a normal track (the answerer's is matched to the offer's audio section)
    stream.getAudioTracks().forEach((t) => {
      peer.audioSender = pc.addTrack(t, stream)
    })

    if (role === 'offerer') {
      // Always reserve a video slot, so turning the camera / screen on later needs no renegotiation
      const transceiver = pc.addTransceiver('video', { direction: 'sendrecv', streams: [stream] })
      peer.videoSender = transceiver.sender

      // If I am presenting right now, the new participant gets my screen
      const track = getOutgoingVideoTrack()
      if (track) transceiver.sender.replaceTrack(track).catch((err) => console.error(err))
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socket.emit('gcall:signal', { callId, to: userId, data: { candidate: e.candidate } })
      }
    }

    const remote = new MediaStream()
    pc.ontrack = (e) => {
      if (!remote.getTracks().some((t) => t.id === e.track.id)) remote.addTrack(e.track)
      setPeerStreams((prev) => ({ ...prev, [userId]: remote }))
    }

    const scheduleRestart = (delay) => {
      peer.retryTimer = setTimeout(() => {
        if (peersRef.current.get(userId) !== peer || pc.connectionState === 'connected') return
        restartPeerIce(callId, userId)
        scheduleRestart(RECONNECT_RETRY)
      }, delay)
    }

    pc.onconnectionstatechange = () => {
      if (peersRef.current.get(userId)?.pc !== pc) return   // stale connection
      const state = pc.connectionState

      if (state === 'connected') {
        clearTimeout(peer.retryTimer)
        clearTimeout(peer.giveUpTimer)
        peer.retryTimer = null
        peer.giveUpTimer = null
        markReconnecting(userId, false)
        announceMedia()   // includes "I am presenting" and my raised hand
        return
      }

      if (state !== 'disconnected' && state !== 'failed') return

      markReconnecting(userId, true)
      if (!peer.retryTimer) scheduleRestart(state === 'failed' ? 0 : RECONNECT_GRACE)
      if (!peer.giveUpTimer) {
        peer.giveUpTimer = setTimeout(() => {
          if (peersRef.current.get(userId)?.pc === pc && pc.connectionState !== 'connected') {
            removePeer(userId)
            setNotice('Lost the connection to a participant. A TURN server may be needed on this network.')
          }
        }, RECONNECT_GIVE_UP)
      }
    }

    peersRef.current.set(userId, peer)
    return peer
  }, [announceMedia, removePeer, getOutgoingVideoTrack, restartPeerIce, markReconnecting])

  const connectToPeer = async (callId, userId) => {
    const peer = createGroupPeer(callId, userId, 'offerer')

    try {
      const offer = await peer.pc.createOffer()
      await peer.pc.setLocalDescription(offer)
      socket.emit('gcall:signal', { callId, to: userId, data: { sdp: peer.pc.localDescription } })
    } catch (err) {
      console.error('Unable to create group call offer', err)
      removePeer(userId)
    }
  }

  // Answerer side: attach my video slot to the video section that came with the offer
  const bindPeerVideo = useCallback(async (peer) => {
    const tr = peer.pc.getTransceivers().find((t) => t.receiver?.track?.kind === 'video')
    if (!tr) return

    tr.direction = 'sendrecv'
    peer.videoSender = tr.sender

    const track = getOutgoingVideoTrack()
    if (track) await tr.sender.replaceTrack(track)
  }, [getOutgoingVideoTrack])

  const clearIncomingGroup = useCallback(() => {
    clearTimeout(groupRingTimer.current)
    setIncomingGroup(null)
  }, [])

  // info = { callId, groupId, groupName, type }. withVideo: true / false (default: follows the call type)
  const joinGroupCall = async (info, withVideo, opts = {}) => {
    if (callRef.current) return setNotice('You are already in a call.')

    clearIncomingGroup()

    // Private meeting: the passcode is needed before connecting
    const live = Object.values(activeRef.current).find((r) => r.callId === info.callId)
    let passcode = null

    if (live?.private) {
      passcode = opts.passcode || null
      const ok = passcode && (await computeKeyCheck(passcode)) === live.keyCheck

      if (!ok) {
        setPasscodePrompt({ info, withVideo, error: passcode ? 'This passcode is not correct.' : '' })
        return
      }
      setPasscodePrompt(null)
    }

    const video = typeof withVideo === 'boolean' ? withVideo : info.type === 'video'

    if (info.mode === 'sfu') {
      try {
        let ack = await emitWithAck('gcall:join', { callId: info.callId })

        if (!ack?.ok && ack?.error === 'already-joined' && ack.canTakeOver) {
          if (window.confirm('You are already in this meeting in another window. Join here instead? The other window will leave.')) {
            ack = await emitWithAck('gcall:join', { callId: info.callId, takeover: true })
          }
        }

        if (!ack?.ok) {
          finish(groupCallError(ack?.error))
          return
        }

        // The meeting turned out to be private but we have no passcode (it changed while we joined)
        if (ack.private && !passcode) {
          socket.emit('gcall:leave', { callId: info.callId })
          finish('This is a private meeting. Join again and enter the passcode.')
          return
        }

        updateCall({
          kind: 'group',
          mode: 'sfu',
          callId: info.callId,
          groupId: ack.groupId || info.groupId,
          groupName: ack.groupName || info.groupName,
          type: ack.type || info.type,
          status: 'connecting',
          connectedAt: ack.startedAt,
        })

        const people = ack.participants || []
        setParticipants(people)
        if (passcode) {
          setIsPrivate(true)
          setPasscode(passcode)
        }        
        setHosts(ack.hosts || [])
        setCanRecord(Boolean(ack.recordingAvailable))
        setRecording(ack.recording || null)
        setCaptionAgent(Boolean(ack.captionAgent))
        setCaptionsActive(Boolean(ack.captionsActive))

        // Big meeting: join with mic and camera off
        const large = people.length >= SFU_JOIN_MUTED_AFTER

        try {
          await connectSfu({ url: ack.lkUrl, token: ack.lkToken, mic: !large, camera: video && !large, passphrase: passcode })
          if (callRef.current?.callId === ack.callId) {
            updateCall({ ...callRef.current, status: 'connected' })
          }
        } catch (err) {
          hangUp('ended')
          setNotice(err?.message || 'Unable to connect to the meeting server.')
        }
      } catch (err) {
        finish(mediaError(err))
      }
      return
    }

    try {
      await loadIceServers()

      await getMedia(video ? 'video' : 'audio')

      const ack = await emitWithAck('gcall:join', { callId: info.callId })
      if (!ack?.ok) {
        finish(groupCallError(ack?.error))
        return
      }

      updateCall({
        kind: 'group',
        callId: info.callId,
        groupId: ack.groupId || info.groupId,
        groupName: ack.groupName || info.groupName,
        type: ack.type || info.type,
        mode: ack.mode || info.mode,
        status: 'connected',
        connectedAt: ack.startedAt,
      })
      setParticipants(ack.participants || [])

      // I am the newest participant: I send an offer to everyone already in the call
      await Promise.all((ack.peers || []).map((p) => connectToPeer(info.callId, p.userId)))

      announceMedia()
    } catch (err) {
      finish(mediaError(err))
    }
  }

  const startGroupCall = async (group, type, opts = {}) => {
    if (callRef.current) return setNotice('You are already in a call.')

    // A call is already running in this group: join it instead of starting another one
    const running = activeRef.current[group._id]
    if (running) {
      return joinGroupCall(
        { 
          callId: running.callId, 
          groupId: group._id, 
          groupName: group.name, 
          type: running.type, 
          mode: running.mode 
        },
        type === 'video'
      )
    }
    let passcode = null
    let keyCheck = null

    if (opts.private) {
      if (!window.isSecureContext || !window.crypto?.subtle) {
        return setNotice('Private meetings need a secure (HTTPS) connection.')
      }
      passcode = generatePasscode()
      keyCheck = await computeKeyCheck(passcode)
    }

    try {
      await loadIceServers()

      const stream = await getMedia(type)
      const actualType = stream.getVideoTracks().length > 0 ? 'video' : 'audio'

      const ack = await emitWithAck('gcall:start', {
        groupId: group._id,
        type: actualType,
        ...(opts.private ? { e2ee: true, keyCheck } : {}),
      })

      if (!ack?.ok) {
        if (ack?.error === 'active') {
          // Somebody started it a moment ago: join theirs
          cleanup()
          return joinGroupCall(
            { callId: ack.callId, groupId: group._id, groupName: group.name, type: actualType, mode: ack.mode },
            actualType === 'video'
          )
        }
        finish(groupCallError(ack?.error))
        return
      }

      if (ack.mode === 'sfu') {
        // The meeting server carries the media: release the stream used for the permission check
        localRef.current?.getTracks().forEach((t) => t.stop())
        localRef.current = null
        setLocalStream(null)

        // Windows keeps a webcam busy for a moment after it is released
        await new Promise((resolve) => setTimeout(resolve, 600))

        updateCall({
          kind: 'group',
          mode: 'sfu',
          callId: ack.callId,
          groupId: group._id,
          groupName: group.name,
          type: actualType,
          status: 'connected',
          connectedAt: ack.startedAt,
        })
        setParticipants(ack.participants || [])
        if (passcode) {
          setIsPrivate(true)
          setPasscode(passcode)
          setPasscodeShare(passcode)          // shows the "share this passcode" dialog to the host
        }        
        setHosts(ack.hosts || [])
        setCanRecord(Boolean(ack.recordingAvailable))
        setRecording(ack.recording || null)        
        setCaptionAgent(Boolean(ack.captionAgent))

        try {
          await connectSfu({ url: ack.lkUrl, token: ack.lkToken, mic: true, camera: actualType === 'video', passphrase: passcode })        } catch (err) {
          hangUp('ended')
          setNotice(err?.message || 'Unable to connect to the meeting server.')
        }
        return
      }

      updateCall({
        kind: 'group',
        callId: ack.callId,
        groupId: group._id,
        groupName: group.name,
        type: actualType,
        status: 'connected',
        connectedAt: ack.startedAt,
      })
      setParticipants(ack.participants || [])
      announceMedia()
    } catch (err) {
      finish(mediaError(err))
    }
  }

  const submitPasscode = (text) => {
    if (!passcodePrompt) return
    joinGroupCall(passcodePrompt.info, passcodePrompt.withVideo, { passcode: normalizePasscode(text) })
  }

  const cancelPasscode = () => setPasscodePrompt(null)

  // Ask the server whether a call is running in a group (used when a chat is opened)
  const refreshGroupCall = useCallback(async (groupId) => {
    if (!groupId) return

    const res = await emitWithAck('gcall:status', { groupId })

    setActiveGroupCalls((prev) => {
      const next = { ...prev }
      if (res?.active) next[groupId] = res.active
      else delete next[groupId]
      return next
    })
  }, [])

  // ======================================================================
  // Local media controls (work for both 1:1 and group)
  // ======================================================================
  const toggleMute = async () => {
    const lp = sfuRoomRef.current?.localParticipant
    if (lp) {
      try { await lp.setMicrophoneEnabled(!lp.isMicrophoneEnabled) } catch (err) { setNotice(mediaError(err)) }
      takeSfuSnapshot()
      return
    }

    const nextMuted = !mutedRef.current
    mutedRef.current = nextMuted
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = !nextMuted })
    setMuted(nextMuted)

    if (callRef.current?.kind === 'group') announceMedia()
  }

  const toggleHand = async () => {
    const next = !handRaisedRef.current
    handRaisedRef.current = next
    setHandRaised(next)

    const lp = sfuRoomRef.current?.localParticipant
    if (lp) {
      try { await lp.setAttributes({ hand: next ? '1' : '0' }) } catch (err) { console.error(err) }
      takeSfuSnapshot()
      return
    }

    announceMedia()
  }

  // Choose a microphone. During a call it switches live (and keeps the mute state).
  const switchMic = async (deviceId) => {
    if (!deviceId) return

    const room = sfuRoomRef.current
    if (room) {
      try {
        await room.switchActiveDevice('audioinput', deviceId)
        save(MIC_KEY, deviceId)
        setMicId(deviceId)
      } catch (err) {
        setNotice(mediaError(err))
      }
      return
    }

    const stream = localRef.current

    // No call (or no stream yet): remember the choice
    if (!callRef.current || !stream) {
      save(MIC_KEY, deviceId)
      setMicId(deviceId)
      return
    }

    const current = stream.getAudioTracks()[0]
    if (trackDeviceId(current) === deviceId) {
      save(MIC_KEY, deviceId)
      setMicId(deviceId)
      return
    }

    if (micBusyRef.current) return
    micBusyRef.current = true

    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      const track = mic.getAudioTracks()[0]
      track.enabled = !mutedRef.current

      for (const sender of audioSenders()) await sender.replaceTrack(track)

      // Release the old microphone only after the new one is live
      stream.getAudioTracks().forEach((t) => t.stop())

      const next = new MediaStream([track, ...stream.getVideoTracks()])
      localRef.current = next
      setLocalStream(next)

      save(MIC_KEY, deviceId)
      setMicId(deviceId)
    } catch (err) {
      if (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError') {
        refreshDevices()
        if (readSaved(MIC_KEY) === deviceId) save(MIC_KEY, '')
      }

      setNotice(mediaError(err))
    } finally {
      micBusyRef.current = false
    }
  }

  // ---------- Screen sharing ----------
  // The screen goes through the SAME video slot the camera uses (replaceTrack, no renegotiation).
  // Only one thing can use the slot at a time, so while presenting the camera is not sent.
  const stopScreenShare = async () => {
    const lp = sfuRoomRef.current?.localParticipant
    if (lp) {
      await lp.setScreenShareEnabled(false)
      takeSfuSnapshot()
      return
    }

    const track = screenTrackRef.current
    if (!track) return

    screenTrackRef.current = null
    track.onended = null
    track.stop()
    setScreenSharing(false)

    // Give the video slot back to the camera (or nothing, if the camera is off)
    const cam = localRef.current?.getVideoTracks()[0] || null
    try {
      for (const sender of videoSenders()) await sender.replaceTrack(cam)
    } catch (err) {
      console.error('Unable to restore the camera after screen sharing', err)
    }

    announceMedia()   // does nothing if the call is already gone
  }

  const startScreenShare = async () => {
    const c = callRef.current

    if (!c || c.status !== 'connected') {
      setNotice('Screen sharing is available once the call is connected.')
      return
    }

    const lp = sfuRoomRef.current?.localParticipant
    if (lp) {
      try {
        await lp.setScreenShareEnabled(true)
      } catch (err) {
        if (err?.name !== 'NotAllowedError') setNotice(err?.message || 'Unable to start screen sharing.')
      }
      takeSfuSnapshot()
      return
    }

    if (screenTrackRef.current || screenBusyRef.current) return

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setNotice('Screen sharing is not supported in this browser.')
      return
    }

    screenBusyRef.current = true

    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
      const track = display.getVideoTracks()[0]

      // The call may have ended while the picker was open
      if (!track || !callRef.current || callRef.current.callId !== c.callId) {
        display.getTracks().forEach((t) => t.stop())
        return
      }

      track.contentHint = 'detail'                 // sharp text matters more than frame rate
      track.onended = () => { stopScreenShare() }  // the browser's own "Stop sharing" button

      screenTrackRef.current = track
      setScreenSharing(true)

      for (const sender of videoSenders()) await sender.replaceTrack(track)

      announceMedia()
    } catch (err) {
      // Pressing Cancel in the browser's picker is normal, not an error
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        console.error('Unable to start screen sharing', err)
        setNotice(err?.message || 'Unable to start screen sharing.')
      }
    } finally {
      screenBusyRef.current = false
    }
  }

  // Camera on/off during a call (this is the audio -> video upgrade)
  const toggleCamera = async () => {
    const c = callRef.current
    if (!c || cameraBusyRef.current) return

    const lp = sfuRoomRef.current?.localParticipant

    // Large meeting: the room is not ready yet, so do NOT fall into the mesh code below
    if (c.mode === 'sfu' && !lp) {
      setNotice('Still connecting to the meeting. Please try again in a moment.')
      return
    }
    
    if (lp) {
      cameraBusyRef.current = true

      try {
        if (lp.isCameraEnabled) await lp.setCameraEnabled(false)
        else await enableSfuCamera(lp, readSaved(CAMERA_KEY))
      } catch (err) {
        setNotice(mediaError(err))
      }

      cameraBusyRef.current = false
      takeSfuSnapshot()
      return
    }

    const senders = videoSenders()

    // 1:1 needs the connection first. In a group call the camera can be turned on even
    // while nobody else has joined yet (new participants then receive it automatically).
    if (c.kind !== 'group' && (!pcRef.current || senders.length === 0)) return

    cameraBusyRef.current = true

    try {
      if (cameraOnRef.current) {
        // Camera OFF. While presenting, the slot keeps carrying the screen.
        if (!screenTrackRef.current) {
          for (const sender of senders) await sender.replaceTrack(null)
        }
        localRef.current.getVideoTracks().forEach((t) => t.stop())   // really release the camera

        const next = new MediaStream(localRef.current.getAudioTracks())
        localRef.current = next
        setLocalStream(next)

        setCamera(false)
        announceMedia()
      } else {
        // Camera ON (uses the camera chosen in the camera menu)
        const cam = await openCamera({ preferredId: readSaved(CAMERA_KEY) })
        const track = cam.getVideoTracks()[0]

        if (!screenTrackRef.current) {
          for (const sender of senders) await sender.replaceTrack(track)
        }

        const next = new MediaStream([...localRef.current.getAudioTracks(), track])
        localRef.current = next
        setLocalStream(next)

        setCamera(true)
        setCameraId(trackDeviceId(track))
        announceMedia()
        refreshDevices()
      }
    } catch (err) {
      setNotice(mediaError(err))
    } finally {
      cameraBusyRef.current = false
    }
  }

  // Choose a camera. If the camera is on, switch live; otherwise remember it for next time.
  const switchCamera = async (deviceId) => {
    if (!deviceId) return

    const room = sfuRoomRef.current
    if (room) {
      try {
        await room.switchActiveDevice('videoinput', deviceId)
        save(CAMERA_KEY, deviceId)
        setCameraId(deviceId)
      } catch (err) {
        setNotice(mediaError(err))
      }
      return
    }
        
    if (!callRef.current || !cameraOnRef.current) {
      save(CAMERA_KEY, deviceId)
      setCameraId(deviceId)
      return
    }

    const senders = videoSenders()
    if (cameraBusyRef.current) return
    if (senders.length === 0 && callRef.current.kind !== 'group') return

    const current = localRef.current?.getVideoTracks()[0]
    if (trackDeviceId(current) === deviceId) return

    cameraBusyRef.current = true

    try {
      const cam = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } })
      const track = cam.getVideoTracks()[0]

      // While presenting, the slot keeps carrying the screen
      if (!screenTrackRef.current) {
        for (const sender of senders) await sender.replaceTrack(track)
      }

      // Release the old camera only after the new one is live
      localRef.current.getVideoTracks().forEach((t) => t.stop())

      const next = new MediaStream([...localRef.current.getAudioTracks(), track])
      localRef.current = next
      setLocalStream(next)

      save(CAMERA_KEY, deviceId)
      setCameraId(deviceId)
    } catch (err) {
      if (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError') {
        refreshDevices()
        if (readSaved(CAMERA_KEY) === deviceId) save(CAMERA_KEY, '')   // forget a camera that no longer exists
      }

      setNotice(mediaError(err))
    } finally {
      cameraBusyRef.current = false
    }
  }

  // ---------- Who is speaking (audio level meter) ----------
  // Keys: 'me', the userId of a group participant, or call.peerId in a 1:1 call.
  const live = Boolean(call) && call.mode !== 'sfu' && (call.status === 'connecting' || call.status === 'connected')

  useEffect(() => {
    if (!live) return

    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return

    const ctx = new Ctx()
    const meters = new Map()   // key -> { trackId, source, analyser, data, lastLoud }
    let last = {}

    const THRESHOLD = 0.03     // RMS level that counts as speech
    const HANGOVER = 400       // ms to keep the highlight after the last loud moment (no flicker)

    const drop = (key) => {
      const m = meters.get(key)
      if (!m) return
      try { m.source.disconnect() } catch { /* already gone */ }
      meters.delete(key)
    }

    const isSpeaking = (key, stream) => {
      const track = stream?.getAudioTracks()[0]
      if (!track || track.readyState === 'ended') {
        drop(key)
        return false
      }

      // (Re)build the meter when the track is new (mic switched, remote track arrived late)
      let m = meters.get(key)
      if (!m || m.trackId !== track.id) {
        drop(key)
        const source = ctx.createMediaStreamSource(new MediaStream([track]))
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)   // not connected to destination: no double sound
        m = { trackId: track.id, source, analyser, data: new Uint8Array(analyser.fftSize), lastLoud: 0 }
        meters.set(key, m)
      }

      m.analyser.getByteTimeDomainData(m.data)
      let sum = 0
      for (let i = 0; i < m.data.length; i++) {
        const x = (m.data[i] - 128) / 128
        sum += x * x
      }
      const rms = Math.sqrt(sum / m.data.length)

      const now = Date.now()
      if (rms > THRESHOLD) m.lastLoud = now
      return now - m.lastLoud < HANGOVER
    }

    const tick = () => {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {})

      const c = callRef.current
      if (!c) return

      const sources = [['me', localRef.current]]
      if (c.kind === 'group') {
        Object.entries(peerStreamsRef.current).forEach((entry) => sources.push(entry))
      } else {
        sources.push([c.peerId, remoteStreamRef.current])
      }

      const next = {}
      const keys = new Set()

      for (const [key, stream] of sources) {
        keys.add(key)
        // A muted microphone is never "speaking"
        if (key === 'me' && mutedRef.current) { drop(key); continue }
        if (isSpeaking(key, stream)) next[key] = true
      }

      // Forget people who left
      for (const key of [...meters.keys()]) if (!keys.has(key)) drop(key)

      // Only re-render when something actually changed
      const a = Object.keys(next)
      if (a.length !== Object.keys(last).length || a.some((k) => !last[k])) {
        last = next
        setSpeaking(next)
      }
    }

    const timer = setInterval(tick, 100)

    return () => {
      clearInterval(timer)
      meters.forEach((m) => { try { m.source.disconnect() } catch { /* ignore */ } })
      meters.clear()
      ctx.close().catch(() => {})
      setSpeaking({})
    }
  }, [live])  

  // Remove captions that were not updated for a while
  useEffect(() => {
    if (!captionsOn) return

    const timer = setInterval(() => {
      setCaptions((prev) => {
        const now = Date.now()
        const next = Object.fromEntries(Object.entries(prev).filter(([, c]) => now - c.at < CAPTION_LINGER))
        return Object.keys(next).length === Object.keys(prev).length ? prev : next
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [captionsOn])

  // Transcribe MY speech while somebody in the call wants captions (and I am not muted)
  const captionCallId = call?.callId
  const captionCallStatus = call?.status
  const myIdStr = auth?._id ? String(auth._id) : ''

  useEffect(() => {
    if (!SpeechRec || !captionCallId || captionCallStatus !== 'connected' || !captionsActive || muted || captionAgent) return

    const rec = new SpeechRec()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = captionLang

    let stopped = false
    let lastSent = 0
    let restartDelay = 300
    let timer = null

    const send = (text, final) => {
      const c = callRef.current
      if (!c || !text) return

      const now = Date.now()
      if (!final && now - lastSent < 250) return      // interim results come very often
      lastSent = now

      pushCaption(myIdStr, 'You', text, final)         // my own caption on my screen

      if (c.mode === 'sfu') {
        const lp = sfuRoomRef.current?.localParticipant
        if (lp) {
          Promise.resolve(
            lp.publishData(new TextEncoder().encode(JSON.stringify({ text, final })), {
              reliable: final,
              topic: 'caption',
            })
          ).catch(() => {})
        }
      } else {
        socket.emit('call:caption', { callId: c.callId, text, final })
      }
    }

    rec.onresult = (e) => {
      restartDelay = 300
      let interim = ''

      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        const text = (r[0]?.transcript || '').trim()
        if (r.isFinal) send(text, true)
        else interim += ` ${text}`
      }

      interim = interim.trim()
      if (interim) send(interim, false)
    }

    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        stopped = true
        setNotice('Captions need microphone access for speech recognition.')
      } else if (e.error === 'language-not-supported') {
        stopped = true
        setNotice('This language is not supported for captions in your browser.')
      } else if (e.error === 'network') {
        restartDelay = 3000
      }
      // 'no-speech' and 'aborted' are normal: onend restarts the recognition
    }

    // The browser stops after silence or about a minute: start it again
    rec.onend = () => {
      if (stopped) return
      timer = setTimeout(() => {
        try { rec.start() } catch { /* already started */ }
      }, restartDelay)
    }

    try { rec.start() } catch { /* ignore */ }

    return () => {
      stopped = true
      clearTimeout(timer)
      rec.onresult = null
      rec.onerror = null
      rec.onend = null
      try { rec.abort() } catch { /* ignore */ }
    }
  }, [captionCallId, captionCallStatus, captionsActive, muted, captionLang, myIdStr, pushCaption])

  // Ringtone: 1:1 incoming, group incoming, or ringback for the caller
  const hasCall = Boolean(call)
  useEffect(() => {
    if ((hasCall && call?.status === 'ringing') || (!hasCall && incomingGroup)) startRing('incoming')
    else if (call?.status === 'calling') startRing('outgoing')
    else stopRing()

    return () => stopRing()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.status, hasCall, incomingGroup])

  // ---------- Socket events ----------
  useEffect(() => {
    if (!auth?._id) return

    // ----- 1:1 -----
    const onIncoming = ({ callId, type, fromUserId, fromName, fromAvatar }) => {
      if (callRef.current) return   // already busy in this tab

      loadIceServers()

      updateCall({
        callId,
        peerId: fromUserId,
        peerName: fromName,
        peerAvatar: fromAvatar || null,
        type,
        direction: 'in',
        status: 'ringing',
      })

      if (document.visibilityState !== 'visible' && Notification.permission === 'granted') {
        new Notification(`Incoming ${type} call`, { body: fromName, icon: '/assets/simbalinkicon.png' })
      }
    }

    const onAccepted = async ({ callId }) => {
      const c = callRef.current
      if (!c || c.callId !== callId || c.direction !== 'out' || !localRef.current) return

      clearTimeout(ringTimer.current)
      updateCall({ ...c, status: 'connecting' })

      try {
        const pc = createPeer(callId, localRef.current, 'caller')
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)
        socket.emit('call:signal', { callId, data: { sdp: pc.localDescription } })
      } catch (err) {
        console.error('Unable to create call offer', err)
        hangUp('ended')
        setNotice('Unable to start the call.')
      }
    }

    const onSignal = async ({ callId, data }) => {
      const c = callRef.current
      if (!c || c.kind === 'group' || c.callId !== callId || !data) return

      // The other side changed their camera / screen-share state
      if (typeof data.camera === 'boolean' || typeof data.screenShare === 'boolean') {
        if (typeof data.camera === 'boolean') setRemoteCamera(data.camera)
        if (typeof data.screenShare === 'boolean') setRemoteScreenSharing(data.screenShare)
        return
      }

      const pc = pcRef.current
      if (!pc) return

      try {
        if (data.sdp) {
          await pc.setRemoteDescription(data.sdp)

          for (const cand of pendingIce.current) await pc.addIceCandidate(cand)
          pendingIce.current = []

          if (data.sdp.type === 'offer') {
            await bindVideoSlot(pc)
            const answer = await pc.createAnswer()
            await pc.setLocalDescription(answer)
            socket.emit('call:signal', { callId, data: { sdp: pc.localDescription } })
          }
        } else if (data.candidate) {
          if (pc.remoteDescription) await pc.addIceCandidate(data.candidate)
          else pendingIce.current.push(data.candidate)
        }
      } catch (err) {
        console.error('Call signalling error', err)
      }
    }

    const onEnded = ({ callId, reason }) => {
      const c = callRef.current
      if (!c || c.kind === 'group' || c.callId !== callId) return

      let message = null
      if (c.status === 'ringing') {
        if (reason === 'missed' || reason === 'disconnected') message = `Missed call from ${c.peerName}.`
      } else if (reason === 'rejected') {
        message = `${c.peerName} declined the call.`
      } else if (reason === 'disconnected') {
        message = 'The call was disconnected.'
      } else if (reason !== 'answered-elsewhere') {
        message = 'Call ended.'
      }

      finish(message)
    }

    // ----- Group -----
    const onGroupIncoming = ({ callId, groupId, groupName, type, fromName, mode }) => {
      if (callRef.current) return   // already in a call

      loadIceServers()

      setIncomingGroup({ callId, groupId, groupName, type, fromName, mode })

      clearTimeout(groupRingTimer.current)
      groupRingTimer.current = setTimeout(() => setIncomingGroup(null), RING_TIMEOUT)

      if (document.visibilityState !== 'visible' && Notification.permission === 'granted') {
        new Notification(`${groupName}: ${type} call`, {
          body: `${fromName} started a call`,
          icon: '/assets/simbalinkicon.png',
        })
      }
    }

    // Live "call in progress" state for every group I belong to
    const onGroupUpdate = ({ groupId, active }) => {
      setActiveGroupCalls((prev) => {
        const next = { ...prev }
        if (active) next[groupId] = active
        else delete next[groupId]
        return next
      })

      if (!active) {
        setIncomingGroup((prev) => (prev && prev.groupId === groupId ? null : prev))
      }

      const c = callRef.current
      if (active && c?.kind === 'group' && c.callId === active.callId) {
        setParticipants(active.participants)
        setHosts(active.hosts || [])
        setRecording(active.recording || null)
      }
    }

    const onGroupSignal = async ({ callId, from, data }) => {
      const c = callRef.current
      if (!c || c.kind !== 'group' || c.callId !== callId || !data) return

      let peer = peersRef.current.get(from)

      if (!peer) {
        // Only an offer can open a new connection (the newest participant always offers)
        if (!data.sdp || data.sdp.type !== 'offer' || !localRef.current) return
        peer = createGroupPeer(callId, from, 'answerer')
      }

      const pc = peer.pc

      try {
        if (data.sdp) {
          await pc.setRemoteDescription(data.sdp)

          for (const cand of peer.pending) await pc.addIceCandidate(cand)
          peer.pending = []

          if (data.sdp.type === 'offer') {
            await bindPeerVideo(peer)
            const answer = await pc.createAnswer()
            await pc.setLocalDescription(answer)
            socket.emit('gcall:signal', { callId, to: from, data: { sdp: pc.localDescription } })
          }
        } else if (data.candidate) {
          if (pc.remoteDescription) await pc.addIceCandidate(data.candidate)
          else peer.pending.push(data.candidate)
        }
      } catch (err) {
        console.error('Group call signalling error', err)
      }
    }

    const onGroupPeerLeft = ({ callId, userId }) => {
      const c = callRef.current
      if (!c || c.kind !== 'group' || c.callId !== callId) return
      removePeer(userId)
    }

    const onGroupMedia = ({ callId, userId, camera, muted: isMuted, screenShare, handRaised: hand }) => {
      const c = callRef.current
      if (!c || c.kind !== 'group' || c.callId !== callId) return

      setPeerMedia((prev) => ({
        ...prev,
        [userId]: { camera, muted: isMuted, screenShare: Boolean(screenShare), handRaised: Boolean(hand) },
      }))
    }

    // The socket dropped: keep the call (the server holds it for 15 s) and try to resume on reconnect
    const onSocketDisconnect = () => {
      if (!callRef.current) return

      markReconnecting('socket', true)
      clearTimeout(socketGiveUpRef.current)
      socketGiveUpRef.current = setTimeout(() => {
        if (!socket.connected && callRef.current) finish('Connection lost. The call was ended.')
      }, 30000)
    }

    const onSocketConnect = async () => {
      const c = callRef.current
      if (!c) return

      clearTimeout(socketGiveUpRef.current)

      // Still ringing or dialing: nothing to resume on the server
      if (c.kind !== 'group' && c.status !== 'connecting' && c.status !== 'connected') {
        markReconnecting('socket', false)
        return
      }

      const res = await emitWithAck(c.kind === 'group' ? 'gcall:resume' : 'call:resume', { callId: c.callId })
      if (callRef.current?.callId !== c.callId) return

      if (res?.ok) {
        markReconnecting('socket', false)
        announceMedia()
        restartAllIce()
      } else {
        finish('The call ended while you were offline.')
      }
    }

    const onCaption = ({ callId, userId, name, text, final }) => {
      const c = callRef.current
      if (!c || c.callId !== callId) return
      pushCaption(userId, name, text, final)
    }

    const onCaptionsActive = ({ callId, active }) => {
      const c = callRef.current
      if (!c || c.callId !== callId) return
      setCaptionsActive(Boolean(active))
    }

    const onGroupNotice = ({ callId, text }) => {
      const c = callRef.current
      if (c && c.callId === callId && text) setNotice(text)
    }

    const onGroupRemoved = ({ callId }) => {
      const c = callRef.current
      if (c && c.callId === callId) finish('You were removed from the meeting by the host.')
    }

    const onGroupMoved = ({ callId }) => {
      const c = callRef.current
      if (c && c.callId === callId) finish('You joined this meeting from another window.')
    }

    socket.on('call:incoming', onIncoming)
    socket.on('call:accepted', onAccepted)
    socket.on('call:signal', onSignal)
    socket.on('call:ended', onEnded)
    socket.on('gcall:incoming', onGroupIncoming)
    socket.on('gcall:update', onGroupUpdate)
    socket.on('gcall:signal', onGroupSignal)
    socket.on('gcall:peer-left', onGroupPeerLeft)
    socket.on('gcall:media', onGroupMedia)
    socket.on('disconnect', onSocketDisconnect)
    socket.on('connect', onSocketConnect)
    socket.on('call:caption', onCaption)
    socket.on('call:captions-active', onCaptionsActive)
    socket.on('gcall:notice', onGroupNotice)
    socket.on('gcall:removed', onGroupRemoved)
    socket.on('gcall:moved', onGroupMoved)

    return () => {
      socket.off('call:incoming', onIncoming)
      socket.off('call:accepted', onAccepted)
      socket.off('call:signal', onSignal)
      socket.off('call:ended', onEnded)
      socket.off('gcall:incoming', onGroupIncoming)
      socket.off('gcall:update', onGroupUpdate)
      socket.off('gcall:signal', onGroupSignal)
      socket.off('gcall:peer-left', onGroupPeerLeft)
      socket.off('gcall:media', onGroupMedia)
      socket.off('disconnect', onSocketDisconnect)
      socket.off('connect', onSocketConnect)
      socket.off('call:caption', onCaption)
      socket.off('call:captions-active', onCaptionsActive)
      socket.off('gcall:notice', onGroupNotice)
      socket.off('gcall:removed', onGroupRemoved)
      socket.off('gcall:moved', onGroupMoved)
    }
    }, [auth?._id, updateCall, createPeer, bindVideoSlot, createGroupPeer, bindPeerVideo, removePeer, hangUp, finish, markReconnecting, restartAllIce, announceMedia, loadIceServers, pushCaption])

  // Leaving the app (logout / unmount) ends any active call
  useEffect(() => () => {
    const c = callRef.current
    if (c?.kind === 'group') socket.emit('gcall:leave', { callId: c.callId })
    else if (c) socket.emit('call:end', { callId: c.callId, reason: 'ended' })

    screenTrackRef.current?.stop()
    localRef.current?.getTracks().forEach((t) => t.stop())
    pcRef.current?.close()
    peersRef.current.forEach((p) => p.pc.close())
    sfuRoomRef.current?.disconnect()
  }, [])

  return (
    <CallContext.Provider
      value={{
        call,
        inCall: Boolean(call),
        localStream,
        remoteStream,
        muted,
        cameraOff,
        remoteCamera,
        cameras,
        cameraId,
        mics,
        micId,
        participants,
        peerStreams,
        peerMedia,
        activeGroupCalls,
        incomingGroup,
        startCall,
        acceptCall,
        rejectCall,
        startGroupCall,
        joinGroupCall,
        dismissIncomingGroup: clearIncomingGroup,
        refreshGroupCall,
        hangUp,
        toggleMute,
        toggleCamera,
        switchCamera,
        switchMic,
        startScreenShare,
        stopScreenShare,
        screenSharing,
        remoteScreenSharing,
        speaking,
        handRaised,
        sfu,
        toggleHand,
        refreshDevices,
        refreshCameras: refreshDevices,   // kept so older code keeps working
        captionsOn,
        captionsSupported: (Boolean(SpeechRec) || captionAgent) && !isPrivate,        captions,
        captionLang,
        toggleCaptions,
        changeCaptionLang,
        captionsActive,                                          // somebody in the call reads captions
        hosts,
        hostAction,
        recording,
        canRecord,
        isPrivate,
        passcode,
        passcodePrompt,
        submitPasscode,
        cancelPasscode,
        passcodeShare,
        dismissPasscodeShare: () => setPasscodeShare(null),        
      }}
    >
      {children}

      <Snackbar
        open={Boolean(notice)}
        message={notice}
        autoHideDuration={5000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </CallContext.Provider>
  )
}